import { forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";

import { Datebox, Form, Textbox, useForm } from "capitalrx-components-2.0/lib/forms";
import { Grid } from "capitalrx-components-2.0/lib/layout";
import { validateDecimal } from "@/App/utils/validation";

interface DirectMemberReimbursementAdditionalPrescriptionInformationFormProps {
  readonly smallFieldsColumns: number;
}

export interface DMRAdditionalPrescriptionInformationForm {
  readonly purchased_date: string;
  readonly prescription_number: string;
  readonly total_paid: number;
  readonly admin_fee: number;
}

export type DirectMemberReimbursementAdditionalPrescriptionInformationFormHandle = {
  isValid: boolean;
  submit: () => Promise<DMRAdditionalPrescriptionInformationForm>;
};

const TRANSLATION_PATH = "DIRECT_MEMBER_REIMBURSEMENT.directMemberReimbursementClaimDetailsForm";

export const DirectMemberReimbursementAdditionalPrescriptionInformationForm = forwardRef<
  DirectMemberReimbursementAdditionalPrescriptionInformationFormHandle,
  DirectMemberReimbursementAdditionalPrescriptionInformationFormProps
>(({ smallFieldsColumns }, ref) => {
  const { t: translate } = useTranslation();
  const form = useForm<DMRAdditionalPrescriptionInformationForm>({
    defaultValues: {},
  });

  useImperativeHandle(ref, () => ({
    isValid: form.formState.isValid,
    submit: () =>
      new Promise<DMRAdditionalPrescriptionInformationForm>((resolve) => {
        form.handleSubmit(
          () => resolve(form.getValues()),
          () => resolve(null),
        )();
      }),
  }));

  return (
    <Form form={form}>
      <Grid>
        <Grid.Span columns={smallFieldsColumns}>
          <Datebox.Field name="purchased_date" label={translate(`${TRANSLATION_PATH}.purchasedFillDate`)} required />
        </Grid.Span>
        <Grid.Break />
        <Grid.Span columns={smallFieldsColumns}>
          <Textbox.Field name="prescription_number" label={translate(`${TRANSLATION_PATH}.prescriptionRxNumber`)} required />
        </Grid.Span>
        <Grid.Break />
        <Grid.Span columns={smallFieldsColumns}>
          <Textbox.Field
            prefix="$"
            name="total_paid"
            label={translate(`${TRANSLATION_PATH}.totalPaid`)}
            instructions={translate(`${TRANSLATION_PATH}.noSpecialCharactersInstruction`)}
            validator={(value) => validateDecimal(value, translate, { maxDecimalPlaces: 2, greaterThan: 0 })}
            required
          />
        </Grid.Span>
        <Grid.Break />
        <Grid.Span columns={smallFieldsColumns}>
          <Textbox.Field
            prefix="$"
            name="admin_fee"
            label={translate(`${TRANSLATION_PATH}.adminFee`)}
            instructions={translate(`${TRANSLATION_PATH}.noSpecialCharactersInstruction`)}
            validator={(value) => validateDecimal(value, translate, { maxDecimalPlaces: 2, greaterThan: 0 })}
            required
          />
        </Grid.Span>
        <Grid.Break />
      </Grid>
    </Form>
  );
});
